// apps/web/src/components/MyData/AttachmentsWeb.jsx
import React, { useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  getDownloadUrl,
  getSignedUrl,
  fetchBlobByUrl,
  openInNewTab,
  reorderAttachments,
  updateAttachmentDisplayTitle,
  uploadAttachments,
  uploadSensitiveAttachments,
} from '../../api/attachmentsClient';
import './AttachmentsWeb.css';
import formatFileName from '@mysimpledb/shared/utils/fileFormatters';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import { useNotify } from '@mysimpledb/shared/context/NotificationContext';
import { useVault } from '@mysimpledb/shared/context/VaultContext';
import { loadSensitiveAttachment } from '../SensitiveAttachmentBlob';
import { isArchiveAttachment } from '../../utils/attachmentUploadTypes';
import { isTarAttachment } from '../../utils/attachmentUploadTypes';
import { partitionFilesByAllowedType } from '../../utils/attachmentUploadTypes';
import { encryptSensitiveAttachment } from '../../utils/crypto/sensitiveAttachmentCrypto';
import { fetchEntryById } from '@mysimpledb/shared/data/fetchEntryById';

// === EAGER imports (avoid code-split vendor chunks that failed to load) ===
import { renderAsync as renderDocxAsync } from 'docx-preview';
import * as XLSX from 'xlsx';

import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import css from 'highlight.js/lib/languages/css';
import sql from 'highlight.js/lib/languages/sql';
import php from 'highlight.js/lib/languages/php';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import xml from 'highlight.js/lib/languages/xml';
import markdown from 'highlight.js/lib/languages/markdown';
import bash from 'highlight.js/lib/languages/bash';
//import 'highlight.js/styles/github.css';
import 'highlight.js/styles/atom-one-light.css';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('jsx', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('tsx', typescript);
hljs.registerLanguage('css', css);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('php', php);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('bash', bash);

import yaml from 'highlight.js/lib/languages/yaml';
hljs.registerLanguage('yaml', yaml);

const PSD_MIME_TYPES = new Set([
  'application/vnd.adobe.photoshop',
  'image/vnd.adobe.photoshop',
  'application/x-photoshop',
  'image/x-photoshop',
  'application/photoshop',
  'application/psd',
  'image/psd',
]);

function isPsdAttachmentFile(file = {}) {
  const name = String(
    file.uploadName ||
      file.name ||
      file.filename_orig ||
      file.filename ||
      ''
  ).toLowerCase();
  const mime = String(
    file.type || file.mimetype || file.mime_type || ''
  ).toLowerCase();

  return name.endsWith('.psd') || PSD_MIME_TYPES.has(mime);
}

function normalizePsdFileForSensitiveUpload(file) {
  if (!isPsdAttachmentFile(file)) return file;

  // A PSD may be reported by the browser as image/vnd.adobe.photoshop.
  // Sensitive attachment encryption generates JPEG derivatives for image/*,
  // which browsers cannot reliably decode from layered PSD data. Force a
  // document-style MIME so PSD remains original-file/download-only.
  if (!String(file?.type || '').toLowerCase().startsWith('image/')) return file;

  const normalized = new File([file], file.name || 'attachment.psd', {
    type: 'application/vnd.adobe.photoshop',
    lastModified: file.lastModified || Date.now(),
  });

  if (file.uploadName) normalized.uploadName = file.uploadName;
  return normalized;
}

export default function AttachmentsWeb({
  entryId,
  files = [],
  token,
  userId,
  selectedAttachmentId,
  onRefresh,
}) {
  const [selectedFile, setSelectedFile] = React.useState(files[0] || null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState(files);
  const [displayTitle, setDisplayTitle] = React.useState('');
  const [savingDisplayTitle, setSavingDisplayTitle] = React.useState(false);
  const [draggedAttachmentId, setDraggedAttachmentId] = React.useState(null);
  const [reorderingAttachments, setReorderingAttachments] =
    React.useState(false);
  const [uploadingAttachments, setUploadingAttachments] = React.useState(false);
  const [uploadWarning, setUploadWarning] = React.useState('');
  const [entryIsSensitive, setEntryIsSensitive] = React.useState(
    Boolean(
      files?.[0]?.sensitiveClientEncrypted ??
        files?.[0]?.sensitive_client_encrypted
    )
  );
  const uploadInputWrapRef = useRef(null);
  const dragStartItemsRef = React.useRef([]);
  const dropCommittedRef = React.useRef(false);

  // generic preview URL for image/pdf
  const [previewUrl, setPreviewUrl] = React.useState(null);

  // DOCX
  const docxContainerRef = React.useRef(null);
  const [docxBlob, setDocxBlob] = React.useState(null);

  // XLSX
  const xlsxWbRef = React.useRef(null);
  const [xlsxSheets, setXlsxSheets] = React.useState([]);
  const [xlsxActive, setXlsxActive] = React.useState(null);
  const [xlsxHtml, setXlsxHtml] = React.useState('');

  // TEXT
  const [textContent, setTextContent] = React.useState('');

  // YOUTUBE
  const [youtubeId, setYoutubeId] = React.useState(null);

  // NEW: store YouTube titles by videoId (minimal change)
  const [youtubeTitles, setYoutubeTitles] = React.useState({});

  // Decrypted client-only metadata for sensitive attachments.
  // This is cleared immediately whenever the vault locks.
  const [sensitiveMetadataById, setSensitiveMetadataById] = React.useState({});
  const sensitiveMetadataRef = React.useRef({});

  // 'image' | 'pdf' | 'docx' | 'xlsx' | 'text' | 'youtube' | 'archive' | 'psd' | 'unsupported' | null
  const [previewKind, setPreviewKind] = React.useState(null);

  const textContainerRef = React.useRef(null);
  const lineNumberRef = React.useRef(null);
  const notify = useNotify();
  const { sensitiveKey, touchVaultActivity } = useVault();

  const [findOpen, setFindOpen] = React.useState(false);
  const [findQuery, setFindQuery] = React.useState('');
  const [activeFindIndex, setActiveFindIndex] = React.useState(0);
  const [findMatchCount, setFindMatchCount] = React.useState(0);

  const findInputRef = React.useRef(null);
  const codeRef = React.useRef(null);


  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024;

  useEffect(() => {
    let cancelled = false;

    const derivedSensitive = Boolean(
      (files || []).some((file) =>
        Boolean(
          file?.sensitiveClientEncrypted ?? file?.sensitive_client_encrypted
        )
      )
    );

    if (derivedSensitive) {
      setEntryIsSensitive(true);
      return () => {
        cancelled = true;
      };
    }

    if (!entryId || !token) {
      setEntryIsSensitive(false);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const entry = await fetchEntryById(entryId, token);
        if (cancelled) return;

        setEntryIsSensitive(
          Boolean(entry?.is_sensitive ?? entry?.isSensitive ?? false)
        );
      } catch (err) {
        if (!cancelled) {
          console.warn(
            'Unable to determine whether entry is sensitive for attachment upload:',
            err
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entryId, files, token]);

  function formatUploadBytes(bytes) {
    const n = Number(bytes || 0);
    if (!Number.isFinite(n)) return '0 Bytes';

    const units = ['Bytes', 'KB', 'MB', 'GB'];
    let value = n;
    let unit = 0;

    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit += 1;
    }

    return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
  }

  function prepareUploadFiles(incomingFiles = []) {
    const existingNames = new Set(
      (items || []).map((file) =>
        String(
          file?.name || file?.filename_orig || file?.filename || ''
        ).toLowerCase()
      )
    );

    const existingBytes = (items || []).reduce(
      (sum, file) => sum + Number(file?.size || file?.size_bytes || 0),
      0
    );

    const psdFiles = incomingFiles.filter(isPsdAttachmentFile);
    const otherFiles = incomingFiles.filter(
      (file) => !isPsdAttachmentFile(file)
    );
    const {
      allowed: otherTypeAllowed,
      rejected: typeRejected,
    } = partitionFilesByAllowedType(otherFiles);

    // PSD is intentionally download-only, but it is an allowed attachment.
    // Keep original incoming order.
    const typeAllowed = incomingFiles.filter(
      (file) =>
        isPsdAttachmentFile(file) || otherTypeAllowed.includes(file)
    );

    const allowed = [];
    const sizeRejected = [];
    let runningBytes = existingBytes;

    typeAllowed.forEach((file) => {
      const size = Number(file?.size || 0);

      if (size > MAX_FILE_SIZE_BYTES) {
        sizeRejected.push({
          file,
          reason: `exceeds the 10 MB per-file limit (${formatUploadBytes(size)})`,
        });
        return;
      }

      if (runningBytes + size > MAX_TOTAL_SIZE_BYTES) {
        sizeRejected.push({
          file,
          reason: 'would exceed the 50 MB total attachment limit for this entry',
        });
        return;
      }

      const match = String(file.name || 'attachment').match(/^(.*?)(\.[^.]+)?$/);
      const base = match?.[1] || String(file.name || 'attachment');
      const ext = match?.[2] || '';
      let uploadName = String(file.name || 'attachment');
      let i = 1;

      while (existingNames.has(uploadName.toLowerCase())) {
        uploadName = `${base}_${i}${ext}`;
        i += 1;
      }

      existingNames.add(uploadName.toLowerCase());
      file.uploadName = uploadName;
      allowed.push(file);
      runningBytes += size;
    });

    return { allowed, typeRejected, sizeRejected };
  }

  const uploadDroppedAttachments = useCallback(
    async (incomingFiles = []) => {
      const effectiveEntryId =
        entryId ?? items?.[0]?.entryId ?? items?.[0]?.entry_id ?? null;

      if (!effectiveEntryId) {
        setUploadWarning('Unable to determine the active entry.');
        return;
      }

      if (!incomingFiles.length || uploadingAttachments) return;

      const { allowed, typeRejected, sizeRejected } =
        prepareUploadFiles(incomingFiles);

      const warnings = [];

      if (typeRejected.length) {
        warnings.push(
          `Unsupported attachment${
            typeRejected.length === 1 ? '' : 's'
          } skipped: ${typeRejected.map((r) => r.file.name).join(', ')}.`
        );
      }

      if (sizeRejected.length) {
        warnings.push(
          `File${sizeRejected.length === 1 ? '' : 's'} skipped: ${sizeRejected
            .map((r) => `${r.file.name} (${r.reason})`)
            .join(', ')}.`
        );
      }

      setUploadWarning(warnings.join(' '));

      if (!allowed.length) return;

      if (entryIsSensitive && !sensitiveKey) {
        notify.show(
          'Unlock the Sensitive Vault before adding attachments to this entry.',
          6000
        );
        return;
      }

      setUploadingAttachments(true);
      setError('');

      try {
        let result;

        if (entryIsSensitive) {
          const encryptedItems = [];

          for (const file of allowed) {
            const encryptionFile =
              normalizePsdFileForSensitiveUpload(file);

            encryptedItems.push(
              await encryptSensitiveAttachment({
                entryId: effectiveEntryId,
                file: encryptionFile,
                vaultKey: sensitiveKey,
              })
            );
          }

          result = await uploadSensitiveAttachments(
            effectiveEntryId,
            encryptedItems,
            token
          );

          touchVaultActivity();
        } else {
          result = await uploadAttachments(effectiveEntryId, allowed, token);
        }

        const uploadedCount =
          result?.attachments?.length || allowed.length;

        await onRefresh?.(effectiveEntryId);

        notify.show(
          `${uploadedCount} attachment${
            uploadedCount === 1 ? '' : 's'
          } added.`,
          3500
        );
      } catch (err) {
        console.error('Attachment-page upload failed:', err);

        const data = err?.response?.data || {};
        const message =
          data?.error ||
          err?.message ||
          'Unable to upload attachment.';

        setUploadWarning(message);
        notify.show(message, 6000);
      } finally {
        setUploadingAttachments(false);
      }
    },
    [
      entryId,
      entryIsSensitive,
      items,
      notify,
      onRefresh,
      sensitiveKey,
      token,
      touchVaultActivity,
      uploadingAttachments,
    ]
  );

  const onAttachmentDrop = useCallback(
    async (acceptedFiles) => {
      await uploadDroppedAttachments(acceptedFiles || []);
    },
    [uploadDroppedAttachments]
  );

  const {
    getRootProps: getUploadRootProps,
    getInputProps: getUploadInputProps,
    isDragActive: isUploadDragActive,
    open: openUploadPicker,
  } = useDropzone({
    onDrop: onAttachmentDrop,
    noClick: true,
    noKeyboard: true,
    disabled: uploadingAttachments,
  });

  const uploadRootProps = getUploadRootProps({
    ref: uploadInputWrapRef,
    className: `att-upload-box${
      isUploadDragActive ? ' is-drag-active' : ''
    }${uploadingAttachments ? ' is-uploading' : ''}`,
    tabIndex: 0,
  });

  const handleUploadPaste = useCallback(
    async (event) => {
      const clipboard = event.clipboardData || window.clipboardData;
      if (!clipboard?.items?.length) return;

      const pastedFiles = [];

      for (let i = 0; i < clipboard.items.length; i += 1) {
        const item = clipboard.items[i];

        if (item.kind !== 'file') continue;

        const file = item.getAsFile?.();
        if (file) pastedFiles.push(file);
      }

      if (!pastedFiles.length) return;

      event.preventDefault();
      await uploadDroppedAttachments(pastedFiles);
    },
    [uploadDroppedAttachments]
  );

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getHighlightedCode(text, language) {
    if (!language || language === 'text' || !hljs.getLanguage(language)) {
      return escapeHtml(text);
    }

    return hljs.highlight(text, {
      language,
      ignoreIllegals: true,
    }).value;
  }

  function getLineCount(text = '') {
    return text ? text.split('\n').length : 1;
  }

  function isCodeFile(file = {}) {
    const ext = String(
      file?.name || file?.filename_orig || file?.filename || ''
    )
      .toLowerCase()
      .split('.')
      .pop();

    return new Set([
      'js',
      'jsx',
      'ts',
      'tsx',
      'php',
      'css',
      'scss',
      'html',
      'htm',
      'json',
      'xml',
      'sql',
      'py',
      'java',
      'c',
      'cpp',
      'cs',
      'go',
      'rb',
      'rs',
      'swift',
      'kt',
      'sh',
      'bash',
      'zsh',
      'yml',
      'yaml',
    ]).has(ext);
  }

  function getCodeLanguage(file = {}) {
    const name = String(
      file?.name || file?.filename_orig || file?.filename || ''
    ).toLowerCase();

    const ext = name.includes('.') ? name.split('.').pop() : '';

    const map = {
      js: 'javascript',
      jsx: 'jsx',
      ts: 'typescript',
      tsx: 'tsx',
      css: 'css',
      scss: 'css',
      sql: 'sql',
      php: 'php',
      py: 'python',
      json: 'json',
      html: 'html',
      htm: 'html',
      xml: 'xml',
      md: 'markdown',
      sh: 'bash',
      bash: 'bash',
      zsh: 'bash',
      yml: 'yaml',
      yaml: 'yaml',
    };

    return map[ext] || 'text';
  }

  async function handleCopyText() {
    if (!textContent) return;

    try {
      await navigator.clipboard.writeText(textContent);
      notify.show('Copied to clipboard');
    } catch (err) {
      console.error('Copy failed:', err);
      setError('Copy failed');
    }
  }

  function goToNextFindMatch() {
    if (findMatchCount <= 0) return;
    setActiveFindIndex((prev) => (prev + 1) % findMatchCount);
  }

  function goToPrevFindMatch() {
    if (findMatchCount <= 0) return;
    setActiveFindIndex((prev) => (prev - 1 + findMatchCount) % findMatchCount);
  }

  function clearFind() {
    setFindQuery('');
    setActiveFindIndex(0);
    setFindMatchCount(0);
    clearFindMarks(codeRef.current);
  }

  function clearFindMarks(root) {
    if (!root) return;

    root.querySelectorAll('mark.att-find-match').forEach((mark) => {
      const text = document.createTextNode(mark.textContent || '');
      mark.replaceWith(text);
    });

    root.normalize();
  }

  function applyFindHighlights(root, query, activeIndex = 0) {
    if (!root) return 0;

    clearFindMarks(root);

    const q = query.trim();
    if (!q) return 0;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    let matchIndex = 0;
    const lowerQ = q.toLowerCase();

    nodes.forEach((node) => {
      const text = node.nodeValue || '';
      const lowerText = text.toLowerCase();

      let start = 0;
      let foundAt = lowerText.indexOf(lowerQ, start);

      if (foundAt === -1) return;

      const frag = document.createDocumentFragment();

      while (foundAt !== -1) {
        if (foundAt > start) {
          frag.appendChild(document.createTextNode(text.slice(start, foundAt)));
        }

        const mark = document.createElement('mark');
        mark.className =
          'att-find-match' + (matchIndex === activeIndex ? ' active' : '');
        mark.textContent = text.slice(foundAt, foundAt + q.length);
        frag.appendChild(mark);

        matchIndex += 1;
        start = foundAt + q.length;
        foundAt = lowerText.indexOf(lowerQ, start);
      }

      if (start < text.length) {
        frag.appendChild(document.createTextNode(text.slice(start)));
      }

      node.replaceWith(frag);
    });

    return matchIndex;
  }

  useEffect(() => {
    function handleKeyDown(e) {
      const isFind = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f';

      if (!isFind) return;

      if (selectedFile && previewKind === 'text') {
        e.preventDefault();
        e.stopPropagation();

        findInputRef.current?.focus();
        findInputRef.current?.select();
      }
    }

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedFile, previewKind]);

  useEffect(() => {
    if (previewKind !== 'text') return;

    const count = applyFindHighlights(
      codeRef.current,
      findQuery,
      activeFindIndex
    );

    setFindMatchCount(count);

    if (count > 0 && activeFindIndex >= count) {
      setActiveFindIndex(0);
      return;
    }

    const active = codeRef.current?.querySelector('.att-find-match.active');
    active?.scrollIntoView({
      block: 'center',
      inline: 'nearest',
    });
  }, [findQuery, activeFindIndex, textContent, previewKind, selectedFile?.id]);

  useEffect(() => setItems(files), [files]);

  useEffect(() => {
    setDisplayTitle(
      String(selectedFile?.displayTitle ?? selectedFile?.display_title ?? '')
    );
  }, [
    selectedFile?.id,
    selectedFile?.displayTitle,
    selectedFile?.display_title,
  ]);

  useEffect(() => {
    sensitiveMetadataRef.current = sensitiveMetadataById;
  }, [sensitiveMetadataById]);

  useEffect(() => {
    let cancelled = false;

    if (!sensitiveKey) {
      sensitiveMetadataRef.current = {};
      setSensitiveMetadataById({});
      return () => {
        cancelled = true;
      };
    }

    const sensitiveFiles = (items || []).filter(
      (file) =>
        Boolean(
          file?.sensitiveClientEncrypted ?? file?.sensitive_client_encrypted
        ) && file?.id != null
    );

    const missingFiles = sensitiveFiles.filter(
      (file) => !sensitiveMetadataRef.current[String(file.id)]
    );

    if (!missingFiles.length) {
      return () => {
        cancelled = true;
      };
    }

    async function loadPrivateMetadata(file) {
      const hasThumb = Boolean(
        file?.hasSensitiveThumb ?? file?.has_sensitive_thumb
      );

      let result;

      if (hasThumb) {
        result = await loadSensitiveAttachment({
          attachmentId: file.id,
          kind: 'thumb',
          token,
          vaultKey: sensitiveKey,
        }).catch(() => null);
      }

      if (!result) {
        result = await loadSensitiveAttachment({
          attachmentId: file.id,
          kind: 'original',
          token,
          vaultKey: sensitiveKey,
        });
      }

      const originalName = String(
        result?.privateMetadata?.originalName || ''
      ).trim();
      const originalMime = String(
        result?.privateMetadata?.originalMime || ''
      ).trim();

      if (!originalName && !originalMime) return null;

      return {
        id: String(file.id),
        originalName,
        originalMime,
      };
    }

    async function loadAllPrivateMetadata() {
      const queue = [...missingFiles];
      const loaded = {};
      const workerCount = Math.min(3, queue.length);

      async function worker() {
        while (!cancelled) {
          const file = queue.shift();
          if (!file) return;

          try {
            const metadata = await loadPrivateMetadata(file);
            if (metadata) loaded[metadata.id] = metadata;
          } catch (err) {
            console.error(
              'Sensitive attachment metadata load failed:',
              file?.id,
              err
            );
          }
        }
      }

      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      if (cancelled || !Object.keys(loaded).length) return;

      setSensitiveMetadataById((current) => {
        const next = { ...current, ...loaded };
        sensitiveMetadataRef.current = next;
        return next;
      });

      touchVaultActivity();
    }

    loadAllPrivateMetadata();

    return () => {
      cancelled = true;
    };
  }, [items, sensitiveKey, token, touchVaultActivity]);

  useEffect(() => {
    if (!selectedAttachmentId || !files?.length) return;

    const match = files.find(
      (file) => Number(file.id) === Number(selectedAttachmentId)
    );

    if (match) {
      setSelectedFile(match);
    }
  }, [selectedAttachmentId, files]);

  // NEW: fetch YouTube titles for list display (minimal change)
  useEffect(() => {
    let cancelled = false;

    async function fetchTitles() {
      const ytFiles = (items || []).filter((f) => pillText(f) === 'VIDEO');
      if (!ytFiles.length) return;

      const ids = ytFiles.map((f) => getYouTubeId(f)).filter(Boolean);

      // only fetch ids we don't already have
      const missing = ids.filter((id) => !youtubeTitles[id]);
      if (!missing.length) return;

      try {
        const results = await Promise.all(
          missing.map(async (id) => {
            // YouTube oEmbed returns { title, ... }
            const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(
              `https://www.youtube.com/watch?v=${id}`
            )}&format=json`;

            const res = await fetch(oembedUrl);
            if (!res.ok) return [id, null];
            const data = await res.json().catch(() => null);
            const title = data?.title ? String(data.title) : null;
            return [id, title];
          })
        );

        if (cancelled) return;

        const next = {};
        for (const [id, title] of results) {
          if (id && title) next[id] = title;
        }

        if (Object.keys(next).length) {
          setYoutubeTitles((prev) => ({ ...prev, ...next }));
        }
      } catch (e) {
        // do nothing: keep existing filename fallback
      }
    }

    fetchTitles();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // keep selection in sync with incoming files
  useEffect(() => {
    if (!selectedFile && files.length) setSelectedFile(files[0]);
    if (selectedFile && !files.find((f) => f.id === selectedFile.id)) {
      setSelectedFile(files[0] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Helpers to categorize the selected file
  const mt = (selectedFile?.mimetype || '').toLowerCase();
  const name = (
    selectedFile?.name ||
    selectedFile?.filename_orig ||
    selectedFile?.filename ||
    ''
  ).toLowerCase();
  const extProvider = (selectedFile?.ext_provider || '').toLowerCase();
  const extUrl = selectedFile?.ext_url || '';

  const isPdf = mt.includes('pdf');
  const isArchive = selectedFile ? isArchiveAttachment(selectedFile) : false;
  const isPsd = selectedFile ? isPsdAttachmentFile(selectedFile) : false;
  const isImage = mt.startsWith('image/') && !isPsd;
  const isDocx =
    mt ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.docx');

  const isXlsx =
    mt ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mt === 'application/vnd.ms-excel' ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls');

  const isText =
    mt.startsWith('text/') ||
    mt === 'application/json' ||
    mt === 'application/xml' ||
    mt === 'application/javascript' ||
    mt === 'application/x-javascript' ||
    /\.(txt|md|csv|json|xml|log|js|jsx|ts|tsx|cjs|mjs|php|py|sql|css|scss|html|htm|java|cs|cpp|c|h|hpp|go|rs|rb|sh|bash|zsh|yml|yaml)$/i.test(
      name
    );

  const isYouTube =
    mt === 'video/youtube' ||
    extProvider === 'youtube' ||
    /(^|\.)youtube\.com$/.test(safeHost(extUrl)) ||
    /(^|\.)youtu\.be$/.test(safeHost(extUrl));

  const isSensitiveAttachment = Boolean(selectedFile?.sensitiveClientEncrypted);

  // XLSX: render active sheet to HTML when switching tabs
  useEffect(() => {
    if (previewKind !== 'xlsx') return;
    if (!xlsxActive || !xlsxWbRef.current) return;

    const ws = xlsxWbRef.current.Sheets[xlsxActive];
    if (!ws) return;

    const html = XLSX.utils.sheet_to_html(ws, { header: '', footer: '' });
    setXlsxHtml(html);
  }, [xlsxActive, previewKind]);

  // DELETE attachment helper (uses the same REACT_APP_API_URL base)
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  async function deleteAttachmentById(id, tokenArg) {
    const res = await fetch(`${API_BASE}/api/attachments/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${tokenArg}`,
      },
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.error || 'Delete failed');
    }

    if (window.syncApi?.disableAttachmentSync) {
      console.log('[sync-delete] disabling local sync', { userId, id });
      const disabled = await window.syncApi.disableAttachmentSync(userId, id);
    } else {
      console.warn('[sync-delete] disableAttachmentSync unavailable');
    }

    if (window.syncApi?.refreshWatcher) {
      console.log('[sync-delete] refreshing watcher');
      const refreshed = await window.syncApi.refreshWatcher(userId);
      console.log('[sync-delete] refresh result', refreshed);

      if (window.syncApi?.getWatchedPaths) {
        const watched = await window.syncApi.getWatchedPaths();
        console.log('[sync-delete] watched after refresh', watched);
      }
    } else {
      console.warn('[sync-delete] refreshWatcher unavailable');
    }
  }

  // Load preview whenever selection changes
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    async function load() {
      setError('');
      setLoading(true);
      setPreviewUrl(null);
      setDocxBlob(null);
      setXlsxHtml('');
      setTextContent('');
      setYoutubeId(null);
      setPreviewKind(null);

      if (!selectedFile) {
        setLoading(false);
        return;
      }

      try {
        // Handle YouTube without hitting /signed-url
        if (isYouTube) {
          const id = getYouTubeId(selectedFile);
          if (!id) {
            setError('Invalid YouTube URL');
          } else {
            setPreviewKind('youtube');
            setYoutubeId(id);
          }
          return;
        }

        if (isSensitiveAttachment) {
          if (!sensitiveKey) {
            setError('Unlock the Sensitive Vault to view this attachment');
            return;
          }

          const original = await loadSensitiveAttachment({
            attachmentId: selectedFile.id,
            kind: 'original',
            token,
            vaultKey: sensitiveKey,
          });
          touchVaultActivity();

          const displayName = String(
            original.privateMetadata?.originalName || 'Sensitive attachment'
          );
          const displayMime = String(
            original.privateMetadata?.originalMime || 'application/octet-stream'
          ).toLowerCase();
          const displayNameLower = displayName.toLowerCase();

          const sensitiveIsArchive = isArchiveAttachment({
            name: displayName,
            type: displayMime,
          });
          const sensitiveIsPsd = isPsdAttachmentFile({
            name: displayName,
            type: displayMime,
          });
          const sensitiveIsImage =
            displayMime.startsWith('image/') && !sensitiveIsPsd;
          const sensitiveIsPdf = displayMime.includes('pdf');
          const sensitiveIsDocx =
            displayMime ===
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            displayNameLower.endsWith('.docx');
          const sensitiveIsXlsx =
            displayMime ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            displayMime === 'application/vnd.ms-excel' ||
            displayNameLower.endsWith('.xlsx') ||
            displayNameLower.endsWith('.xls');
          const sensitiveIsText =
            displayMime.startsWith('text/') ||
            displayMime === 'application/json' ||
            displayMime === 'application/xml' ||
            displayMime === 'application/javascript' ||
            displayMime === 'application/x-javascript' ||
            /\.(txt|md|csv|json|xml|log|js|jsx|ts|tsx|cjs|mjs|php|py|sql|css|scss|html|htm|java|cs|cpp|c|h|hpp|go|rs|rb|sh|bash|zsh|yml|yaml)$/i.test(
              displayNameLower
            );

          if (sensitiveIsArchive) {
            setPreviewKind('archive');
            return;
          }

          if (sensitiveIsPsd) {
            setPreviewKind('psd');
            return;
          }

          if (sensitiveIsImage) {
            const preview = await loadSensitiveAttachment({
              attachmentId: selectedFile.id,
              kind: 'preview',
              token,
              vaultKey: sensitiveKey,
            }).catch(() => original);
            objectUrl = URL.createObjectURL(preview.blob);
            setPreviewKind('image');
            setPreviewUrl(objectUrl);
            return;
          }

          if (sensitiveIsPdf) {
            objectUrl = URL.createObjectURL(original.blob);
            setPreviewKind('pdf');
            setPreviewUrl(objectUrl);
            return;
          }

          if (sensitiveIsDocx) {
            setPreviewKind('docx');
            setDocxBlob(original.blob);
            return;
          }

          if (sensitiveIsXlsx) {
            const ab = await original.blob.arrayBuffer();
            const wb = XLSX.read(ab, { type: 'array' });
            const names = wb.SheetNames || [];
            xlsxWbRef.current = wb;
            const first = names[0] ?? null;
            setXlsxSheets(names);
            setXlsxActive(first);
            if (!first) throw new Error('Empty workbook');
            setPreviewKind('xlsx');
            setXlsxHtml(
              XLSX.utils.sheet_to_html(wb.Sheets[first], {
                header: '',
                footer: '',
              })
            );
            return;
          }

          if (sensitiveIsText) {
            setPreviewKind('text');
            setTextContent(await original.blob.text());
            return;
          }

          setPreviewKind('unsupported');
          return;
        }

        if (isArchive) {
          setPreviewKind('archive');
          return;
        }

        if (isPsd) {
          setPreviewKind('psd');
          return;
        }

        const signed = await getSignedUrl(selectedFile.id, 'inline', token);

        // IMAGES / PDF -> signed URL
        if (isImage || isPdf) {
          setPreviewKind(isImage ? 'image' : 'pdf');
          setPreviewUrl(signed);
          return;
        }

        // DOCX -> keep blob, render with docx-preview in another effect
        if (isDocx) {
          const blob = await fetchBlobByUrl(signed, token);
          if (!cancelled) {
            setPreviewKind('docx');
            setDocxBlob(blob);
          }
          return;
        }

        // XLSX -> parse to simple HTML table with xlsx
        if (isXlsx) {
          const blob = await fetchBlobByUrl(signed, token);
          const ab = await blob.arrayBuffer();
          const wb = XLSX.read(ab, { type: 'array' });
          const names = wb.SheetNames || [];
          xlsxWbRef.current = wb;
          const first = names[0] ?? null;
          setXlsxSheets(names);
          setXlsxActive(first);
          if (!first) throw new Error('Empty workbook');
          setPreviewKind('xlsx');
          setXlsxHtml(
            XLSX.utils.sheet_to_html(wb.Sheets[first], {
              header: '',
              footer: '',
            })
          );
          return;
        }

        // TEXT -> read as text and render inside <pre>
        if (isText) {
          const blob = await fetchBlobByUrl(signed, token);
          const txt = await blob.text();
          if (!cancelled) {
            setPreviewKind('text');
            setTextContent(txt);

            requestAnimationFrame(() => {
              if (textContainerRef.current) {
                textContainerRef.current.scrollTop = 0;
                textContainerRef.current.scrollLeft = 0;
              }
            });
          }
          return;
        }

        console.log('[attachment-preview]', {
          name,
          mt,
          isText,
          selectedFile,
        });

        // Fallback
        if (!cancelled) setPreviewKind('unsupported');
      } catch (e) {
        if (!cancelled) {
          console.error('Preview error:', e);
          setError('Preview failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile?.id, token, isYouTube, sensitiveKey, touchVaultActivity]);

  // Render DOCX when we have a blob (eager lib import; no chunk load)
  useEffect(() => {
    let active = true;
    async function renderDocxNow() {
      if (previewKind !== 'docx') return;
      if (!docxBlob) return;
      const container = docxContainerRef.current;
      if (!container) return;

      container.innerHTML = '';
      try {
        const buf = await docxBlob.arrayBuffer();
        await renderDocxAsync(buf, container, null, {
          className: 'docx',
          inWrapper: false,
          ignoreWidth: true,
          breakPages: false,
        });

        // Fit to width & ensure scroll top
        requestAnimationFrame(() => {
          const c = docxContainerRef.current;
          if (!c) return;
          c.scrollTop = 0;
          c.scrollLeft = 0;

          const firstPage = c.querySelector('.docx-page');
          if (!firstPage) return;

          const containerW = c.clientWidth - 16;
          const pageW = firstPage.getBoundingClientRect().width || 1;
          const scale = Math.min(1, containerW / pageW);

          let zoom = c.querySelector('.docx-zoom');
          if (!zoom) {
            zoom = document.createElement('div');
            zoom.className = 'docx-zoom';
            while (c.firstChild) zoom.appendChild(c.firstChild);
            c.appendChild(zoom);
          }
          zoom.style.transform = `scale(${scale})`;
        });
      } catch (err) {
        console.error('DOCX render error', err);
        if (active && docxContainerRef.current) {
          docxContainerRef.current.innerHTML =
            '<div style="padding:12px;color:#b00">Unable to preview this DOCX.</div>';
        }
      }
    }
    renderDocxNow();
    return () => {
      active = false;
      if (docxContainerRef.current) docxContainerRef.current.innerHTML = '';
    };
  }, [previewKind, docxBlob]);

  const handleOpen = async () => {
    if (!selectedFile) return;

    if (isYouTube) {
      const id = getYouTubeId(selectedFile);
      const url =
        selectedFile?.ext_url ||
        (id ? `https://www.youtube.com/watch?v=${id}` : null);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (isSensitiveAttachment) {
      if (!sensitiveKey) {
        setError('Unlock the Sensitive Vault to open this attachment');
        return;
      }
      const { blob } = await loadSensitiveAttachment({
        attachmentId: selectedFile.id,
        kind: 'original',
        token,
        vaultKey: sensitiveKey,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      touchVaultActivity();
      return;
    }

    await openInNewTab(selectedFile.id, token);
  };

  const handleDownload = async () => {
    if (!selectedFile) return;

    if (isYouTube) {
      handleOpen();
      return;
    }

    try {
      if (isSensitiveAttachment) {
        if (!sensitiveKey) {
          throw new Error(
            'Unlock the Sensitive Vault to download this attachment'
          );
        }
        const { blob, privateMetadata } = await loadSensitiveAttachment({
          attachmentId: selectedFile.id,
          kind: 'original',
          token,
          vaultKey: sensitiveKey,
        });
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download =
          privateMetadata?.originalName || 'sensitive-attachment';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
        touchVaultActivity();
      } else {
        console.log('download click start');
        const url = await getDownloadUrl(selectedFile.id, token);
        console.log('download url received', url);
        window.location.assign(url);
      }
    } catch (err) {
      console.error('Download error', err);
      setError('Download failed');
    }
  };

  const handleSaveDisplayTitle = async () => {
    if (!selectedFile || savingDisplayTitle) return;

    const isSensitive = Boolean(
      selectedFile?.sensitiveClientEncrypted ??
        selectedFile?.sensitive_client_encrypted
    );

    if (isSensitive) {
      notify.show(
        'Custom attachment titles are not available for Sensitive Vault attachments yet.'
      );
      return;
    }

    const ownerId = selectedFile?.ownerId ?? selectedFile?.owner_id;
    if (Number(ownerId) !== Number(userId)) {
      notify.show('Only the attachment owner can change its title.');
      return;
    }

    const nextTitle = displayTitle.trim();

    setSavingDisplayTitle(true);
    setError('');

    try {
      const updated = await updateAttachmentDisplayTitle(
        selectedFile.id,
        nextTitle,
        token
      );

      const savedTitle = updated?.displayTitle ?? '';

      setItems((prev) =>
        prev.map((file) =>
          Number(file.id) === Number(selectedFile.id)
            ? { ...file, displayTitle: savedTitle }
            : file
        )
      );

      setSelectedFile((prev) =>
        prev ? { ...prev, displayTitle: savedTitle } : prev
      );
      setDisplayTitle(savedTitle);

      await onRefresh?.();

      notify.show(
        savedTitle ? 'Attachment title saved.' : 'Attachment title removed.'
      );
    } catch (err) {
      console.error('Attachment title update failed', err);
      setError(
        err?.response?.data?.error || 'Unable to update attachment title'
      );
    } finally {
      setSavingDisplayTitle(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedFile) return;
    const fname = getDisplayFile(selectedFile).name || 'this file';
    const ok = window.confirm(`Delete ${fname}? This cannot be undone.`);
    if (!ok) return;

    setLoading(true);
    setError('');

    try {
      await deleteAttachmentById(selectedFile.id, token);

      const deletedId = selectedFile.id;
      setItems((prev) => prev.filter((f) => f.id !== deletedId));

      // Clear current preview
      setSelectedFile(null);
      setPreviewUrl(null);
      setXlsxHtml('');
      setPreviewKind(null);
      setTextContent('');
      setDocxBlob(null);
      setYoutubeId(null);

      onRefresh?.();
    } catch (e) {
      console.error('Delete error', e);
      setError(e.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  function getDisplayFile(file = {}) {
    const metadata = sensitiveMetadataById[String(file?.id)] || null;
    if (!metadata) return file;

    return {
      ...file,
      name:
        metadata.originalName ||
        file?.name ||
        file?.filename_orig ||
        file?.filename,
      filename_orig:
        metadata.originalName ||
        file?.filename_orig ||
        file?.name ||
        file?.filename,
      mimetype: metadata.originalMime || file?.mimetype,
    };
  }

  const canReorderAttachments =
    items.length > 1 &&
    items.every(
      (file) => Number(file?.ownerId ?? file?.owner_id) === Number(userId)
    );

  function handleAttachmentDragStart(event, attachmentId) {
    if (!canReorderAttachments || reorderingAttachments) {
      event.preventDefault();
      return;
    }

    dragStartItemsRef.current = [...items];
    dropCommittedRef.current = false;
    setDraggedAttachmentId(attachmentId);

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(attachmentId));
  }

  function handleAttachmentDragOver(event, targetAttachmentId) {
    if (!draggedAttachmentId || reorderingAttachments) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    setItems((current) => {
      const fromIndex = current.findIndex(
        (file) => Number(file.id) === Number(draggedAttachmentId)
      );
      const toIndex = current.findIndex(
        (file) => Number(file.id) === Number(targetAttachmentId)
      );

      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }

  async function handleAttachmentDrop(event) {
    if (!draggedAttachmentId || reorderingAttachments) return;

    event.preventDefault();
    dropCommittedRef.current = true;

    const effectiveEntryId =
      entryId ?? items[0]?.entryId ?? items[0]?.entry_id ?? null;
    const attachmentIds = items.map((file) => Number(file.id));

    if (!effectiveEntryId || attachmentIds.some((id) => !Number.isFinite(id))) {
      setItems(dragStartItemsRef.current);
      setDraggedAttachmentId(null);
      notify.show('Unable to determine attachment order.');
      return;
    }

    setReorderingAttachments(true);
    setError('');

    try {
      await reorderAttachments(effectiveEntryId, attachmentIds, token);
      await onRefresh?.(effectiveEntryId);
      notify.show('Attachment order saved.');
    } catch (err) {
      console.error('Attachment reorder failed', err);
      setItems(dragStartItemsRef.current);
      setError(err?.response?.data?.error || 'Unable to save attachment order');
    } finally {
      setReorderingAttachments(false);
      setDraggedAttachmentId(null);
    }
  }

  function handleAttachmentDragEnd() {
    if (!dropCommittedRef.current && dragStartItemsRef.current.length) {
      setItems(dragStartItemsRef.current);
    }

    dropCommittedRef.current = false;
    setDraggedAttachmentId(null);
  }

  // --- UI helpers -----------------------------------------------------------

  function pillText(file = {}) {
    const mimetype = (file?.mimetype || '').toLowerCase();
    const nm = (
      file?.name ||
      file?.filename_orig ||
      file?.filename ||
      ''
    ).toLowerCase();

    const ext = nm.includes('.') ? nm.split('.').pop() : '';

    // Show actual code/text extension when possible
    const textExtensions = new Set([
      'txt',
      'md',
      'markdown',
      'csv',
      'tsv',
      'log',
      'js',
      'jsx',
      'ts',
      'tsx',
      'cjs',
      'mjs',
      'php',
      'py',
      'rb',
      'java',
      'cs',
      'c',
      'cpp',
      'h',
      'hpp',
      'go',
      'rs',
      'sql',
      'html',
      'htm',
      'css',
      'scss',
      'json',
      'xml',
      'yml',
      'yaml',
      'sh',
      'bash',
      'zsh',
    ]);

    if (textExtensions.has(ext)) {
      return ext.toUpperCase();
    }

    if (
      mimetype.startsWith('text/') ||
      mimetype === 'application/javascript' ||
      mimetype === 'application/x-javascript' ||
      mimetype === 'application/json' ||
      mimetype === 'application/xml'
    ) {
      return 'TXT';
    }

    if (ext === 'psd' || PSD_MIME_TYPES.has(mimetype)) return 'PSD';

    if (mimetype === 'application/pdf') return 'PDF';

    if (
      mimetype.includes('word') ||
      mimetype.includes('officedocument.wordprocessingml')
    ) {
      return 'DOC';
    }

    if (mimetype.includes('excel') || mimetype.includes('spreadsheetml')) {
      return 'XLS';
    }

    if (mimetype.startsWith('image/')) return 'IMG';

    const provider = (file.ext_provider || '').toLowerCase();
    const host = safeHost(file.ext_url || '');

    const isYouTubeFile =
      mimetype === 'video/youtube' ||
      provider === 'youtube' ||
      /(^|\.)youtube\.com$/.test(host) ||
      /(^|\.)youtu\.be$/.test(host) ||
      /^youtube[\s_-]/i.test(nm);

    if (isYouTubeFile) {
      return 'VIDEO';
    }

    return 'FILE';
  }

  function safeHost(url) {
    try {
      return new URL(url).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }

  function parseExtMetaVideoId(ext_meta) {
    try {
      if (!ext_meta) return null;
      const meta =
        typeof ext_meta === 'string' ? JSON.parse(ext_meta) : ext_meta;
      const raw = meta?.videoId;
      if (typeof raw === 'string' && raw.trim()) return raw.trim();
    } catch (e) {
      console.warn('ext_meta parse error', e);
    }
    return null;
  }

  function getYouTubeId(file) {
    const fromMeta = parseExtMetaVideoId(file?.ext_meta);
    if (fromMeta) return fromMeta;

    const url = file?.ext_url || '';
    if (url) {
      try {
        const u = new URL(url);
        const host = u.hostname.toLowerCase();

        if (host === 'youtu.be') {
          const part = u.pathname.slice(1).split('/')[0];
          if (part) return part;
        }

        if (host.endsWith('youtube.com')) {
          if (u.pathname.startsWith('/watch')) {
            const v = u.searchParams.get('v');
            if (v) return v;
          }
          if (u.pathname.startsWith('/embed/')) {
            const part = u.pathname.split('/')[2];
            if (part) return part;
          }
          if (u.pathname.startsWith('/shorts/')) {
            const part = u.pathname.split('/')[2] || u.pathname.split('/')[1];
            if (part) return part;
          }
        }
      } catch (e) {
        console.warn('URL parse fallback', e);
      }

      const m = String(url).match(/(?:v=|\/embed\/|youtu\.be\/)([\w-]{6,})/i);
      if (m?.[1]) return m[1];
    }

    const nm = String(file?.name || '');
    const m2 = nm.match(/\bYouTube\s+([A-Za-z0-9_-]{6,})\b/i);
    if (m2?.[1]) return m2[1];

    return null;
  }

  // --- render ---------------------------------------------------------------
  const showLineNumbers = previewKind === 'text' && isCodeFile(selectedFile);
  const lineCount = getLineCount(textContent);
  const lineNumberWidth = `${Math.max(
    36,
    String(lineCount).length * 10 + 18
  )}px`;

  return (
    <div className="att-grid">
      {/* LEFT: list */}
      <div className="att-card att-list-card">
        <div className="att-header">
          <h3 className="att-title">Attachments</h3>
          <AppButton
            variant="outline"
            size="sm"
            onPress={() => onRefresh?.()}
            style={{ width: 100 }}
          >
            Refresh
          </AppButton>
        </div>

        <div className="att-list">
          {items.map((file) => {
            const displayFile = getDisplayFile(file);
            const isYT = pillText(displayFile) === 'VIDEO';
            const vid = isYT ? getYouTubeId(displayFile) : null;
            const ytTitle = vid ? youtubeTitles[vid] : null;
            const displayName =
              displayFile.displayTitle ||
              displayFile.display_title ||
              ytTitle ||
              displayFile.name ||
              displayFile.filename_orig ||
              displayFile.filename ||
              'Attachment';

            const isSelected = selectedFile?.id === file.id;
            const isDragging = Number(draggedAttachmentId) === Number(file.id);

            return (
              <div
                key={file.id}
                className={
                  'att-row' +
                  (isSelected ? ' selected' : '') +
                  (isDragging ? ' dragging' : '')
                }
                onDragOver={(event) => handleAttachmentDragOver(event, file.id)}
                onDrop={handleAttachmentDrop}
              >
                <button
                  type="button"
                  className="att-drag-handle"
                  draggable={canReorderAttachments && !reorderingAttachments}
                  disabled={!canReorderAttachments || reorderingAttachments}
                  onDragStart={(event) =>
                    handleAttachmentDragStart(event, file.id)
                  }
                  onDragEnd={handleAttachmentDragEnd}
                  onClick={(event) => event.stopPropagation()}
                  aria-label={`Reorder ${displayName}`}
                  title={
                    canReorderAttachments
                      ? `Drag to reorder ${displayName}`
                      : 'Only the attachment owner can reorder attachments'
                  }
                >
                  <span aria-hidden="true">⋮⋮</span>
                </button>

                <button
                  type="button"
                  className="att-row-main"
                  onClick={() => {
                    clearFind();
                    setSelectedFile(file);
                  }}
                  onDoubleClick={handleOpen}
                  title={displayName}
                >
                  <span className="name">{formatFileName(displayName)}</span>
                  <span className="att-pill">{pillText(displayFile)}</span>
                </button>
              </div>
            );
          })}
          {!items.length && (
            <div className="att-placeholder">No attachments</div>
          )}
        </div>
      </div>

      <div className="att-card">
        <div
          className={`att-header att-preview-header ${
            previewKind === 'text' ? 'has-find' : ''
          }`}
        >
          <div className="att-display-title-editor">
            <label htmlFor="attachment-display-title">Title</label>
            <input
              id="attachment-display-title"
              type="text"
              value={displayTitle}
              maxLength={255}
              disabled={
                !selectedFile ||
                savingDisplayTitle ||
                Boolean(
                  selectedFile?.sensitiveClientEncrypted ??
                    selectedFile?.sensitive_client_encrypted
                ) ||
                Number(selectedFile?.ownerId ?? selectedFile?.owner_id) !==
                  Number(userId)
              }
              placeholder="Add attachment title…"
              onChange={(e) => setDisplayTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveDisplayTitle();
                }
              }}
            />
            <AppButton
              variant="outline"
              size="sm"
              disabled={
                !selectedFile ||
                savingDisplayTitle ||
                Boolean(
                  selectedFile?.sensitiveClientEncrypted ??
                    selectedFile?.sensitive_client_encrypted
                ) ||
                Number(selectedFile?.ownerId ?? selectedFile?.owner_id) !==
                  Number(userId)
              }
              onPress={handleSaveDisplayTitle}
              style={{ width: 70 }}
            >
              {savingDisplayTitle ? 'Saving…' : 'Save'}
            </AppButton>
          </div>
          <div className="att-preview-actions">
            {previewKind === 'text' && (
              <div className="att-header-find">
                <input
                  ref={findInputRef}
                  value={findQuery}
                  onChange={(e) => {
                    setFindQuery(e.target.value);
                    setActiveFindIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.shiftKey ? goToPrevFindMatch() : goToNextFindMatch();
                    }

                    if (e.key === 'Escape') {
                      clearFind();
                    }
                  }}
                  placeholder="Find in file"
                />

                <span className="att-find-count">
                  {findQuery.trim()
                    ? findMatchCount
                      ? `${activeFindIndex + 1} of ${findMatchCount}`
                      : '0 of 0'
                    : ''}
                </span>

                <button
                  type="button"
                  onClick={goToPrevFindMatch}
                  disabled={!findMatchCount}
                >
                  ↑
                </button>

                <button
                  type="button"
                  onClick={goToNextFindMatch}
                  disabled={!findMatchCount}
                >
                  ↓
                </button>

                <button type="button" onClick={clearFind} disabled={!findQuery}>
                  ×
                </button>
              </div>
            )}
            <AppButton
              variant="solid"
              size="sm"
              disabled={!selectedFile || previewKind !== 'text' || !textContent}
              onPress={handleCopyText}
              style={{ width: 100 }}
            >
              Copy
            </AppButton>
            <AppButton
              variant="solid"
              size="sm"
              disabled={
                !selectedFile ||
                previewKind === 'archive' ||
                previewKind === 'psd'
              }
              onPress={handleOpen}
              style={{ width: 100 }}
            >
              Open
            </AppButton>

            <AppButton
              variant="solid"
              size="sm"
              disabled={!selectedFile || isYouTube}
              onPress={handleDownload}
              style={{ width: 100 }}
            >
              Download
            </AppButton>

            <AppButton
              variant="danger"
              size="sm"
              disabled={!selectedFile || loading}
              onPress={handleDelete}
              style={{ width: 100 }}
            >
              Delete
            </AppButton>
          </div>
        </div>

        <div className="att-body">
          <div className="att-preview">
            {!selectedFile && (
              <div className="att-placeholder">Select a file to preview</div>
            )}
            {selectedFile && loading && (
              <div className="att-placeholder">Loading…</div>
            )}
            {selectedFile && !loading && error && (
              <div className="att-placeholder">{error}</div>
            )}

            {/* IMAGE / PDF */}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'image' &&
              previewUrl && (
                <img
                  alt={selectedFile.name}
                  src={previewUrl}
                  className="att-img"
                />
              )}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'pdf' &&
              previewUrl && (
                <iframe
                  title={selectedFile.name}
                  src={previewUrl}
                  className="att-iframe"
                />
              )}

            {/* DOCX */}
            {selectedFile && !loading && !error && previewKind === 'docx' && (
              <div
                ref={docxContainerRef}
                className="docx-holder"
                style={{ height: '100%', overflow: 'auto' }}
              />
            )}

            {/* XLSX */}
            {selectedFile && !loading && !error && previewKind === 'xlsx' && (
              <div className="xlsx-preview">
                {xlsxSheets.length > 1 && (
                  <div className="xlsx-tabs">
                    {xlsxSheets.map((nm) => (
                      <button
                        key={nm}
                        type="button"
                        className={
                          'xlsx-tab' + (nm === xlsxActive ? ' active' : '')
                        }
                        onClick={() => setXlsxActive(nm)}
                        title={nm}
                      >
                        {nm}
                      </button>
                    ))}
                  </div>
                )}

                <div
                  className="xlsx-html"
                  dangerouslySetInnerHTML={{ __html: xlsxHtml }}
                />
              </div>
            )}

            {/* TEXT / CODE */}
            {selectedFile && !loading && !error && previewKind === 'text' && (
              <div
                className={`att-code-container ${
                  showLineNumbers ? 'with-line-numbers' : 'plain-text'
                }`}
                style={{ '--line-number-width': lineNumberWidth }}
                tabIndex={0}
                onMouseDown={(e) => e.currentTarget.focus()}
                onKeyDownCapture={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
                    e.preventDefault();
                    e.stopPropagation();
                    findInputRef.current?.focus();
                  }
                }}
              >
                {showLineNumbers && (
                  <div ref={lineNumberRef} className="att-line-numbers">
                    {Array.from(
                      { length: getLineCount(textContent) },
                      (_, i) => (
                        <div key={i} className="att-line-number">
                          {i + 1}
                        </div>
                      )
                    )}
                  </div>
                )}

                <pre
                  ref={textContainerRef}
                  className={`att-pre hljs language-${getCodeLanguage(
                    selectedFile
                  )}`}
                  onScroll={(e) => {
                    if (showLineNumbers && lineNumberRef.current) {
                      lineNumberRef.current.scrollTop =
                        e.currentTarget.scrollTop;
                    }
                  }}
                >
                  <code
                    ref={codeRef}
                    dangerouslySetInnerHTML={{
                      __html: getHighlightedCode(
                        textContent,
                        getCodeLanguage(selectedFile)
                      ),
                    }}
                  />
                </pre>
              </div>
            )}

            {/* YOUTUBE */}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'youtube' &&
              youtubeId && (
                <div
                  className="yt-wrap"
                  style={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    background: '#000',
                    borderRadius: 12,
                    overflow: 'hidden',
                  }}
                >
                  <iframe
                    title={selectedFile.name || 'YouTube'}
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 0,
                      display: 'block',
                    }}
                  />
                </div>
              )}

            {/* Archive download-only */}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'archive' && (
                <div className="att-placeholder">
                  Archive file preview is not available. Use{' '}
                  <strong>Download</strong> to save and open the archive on your
                  device.
                </div>
              )}

            {/* Photoshop PSD download-only */}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'psd' && (
                <div className="att-placeholder">
                  Photoshop PSD files are stored as the original layered file.
                  Preview is not available. Use <strong>Download</strong> to open
                  the file in Photoshop or another PSD-compatible application.
                </div>
              )}

            {/* Unsupported */}
            {selectedFile &&
              !loading &&
              !error &&
              previewKind === 'unsupported' && (
                <div className="att-placeholder">
                  Preview not supported. Use <strong>Open</strong> or{' '}
                  <strong>Download</strong>.
                </div>
              )}
          </div>

          <div
            {...uploadRootProps}
            onPaste={handleUploadPaste}
            onClick={() => uploadInputWrapRef.current?.focus()}
          >
            <input {...getUploadInputProps()} />

            {uploadingAttachments ? (
              <div className="att-upload-content">
                <div className="att-upload-title">Uploading attachments…</div>
                <div className="att-upload-subtitle">Please wait.</div>
              </div>
            ) : isUploadDragActive ? (
              <div className="att-upload-content">
                <div className="att-upload-title">Drop files here to add them</div>
                <div className="att-upload-subtitle">
                  They will be attached to the active entry.
                </div>
              </div>
            ) : (
              <div className="att-upload-content">
                <div className="att-upload-title">
                  Drag &amp; drop files here,
                  <br />
                  click the button, or paste an image
                </div>
                <AppButton
                  variant="outline"
                  size="sm"
                  onPress={(event) => {
                    event?.stopPropagation?.();
                    openUploadPicker();
                  }}
                  style={{ width: 116 }}
                >
                  Browse Files
                </AppButton>
              </div>
            )}
          </div>

          {uploadWarning && (
            <div className="att-upload-warning">{uploadWarning}</div>
          )}
        </div>
      </div>
    </div>
  );
}
